import * as React from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import TooltippedElement from "components/TooltippedElement";
import {
  VENDOR_ITEM_NOTES_MAX_LENGTH,
  clampVendorItemNotes,
} from "views/vendors/constants/vendorItemFields";
import styles from "./VendorItemNotesInput.module.scss";

type PanelPosition = {
  top: number;
  left: number;
  width: number;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  onExpandedChange?: (expanded: boolean) => void;
};

export default function VendorItemNotesInput({
  value,
  onChange,
  disabled = false,
  className = "border px-2 py-1 rounded text-sm w-full resize-none h-[40px] min-h-[40px] max-h-[40px] box-border",
  placeholder,
  onExpandedChange,
}: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [panelPosition, setPanelPosition] = useState<PanelPosition | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const setExpandedState = useCallback(
    (next: boolean) => {
      setExpanded(next);
      onExpandedChange?.(next);
    },
    [onExpandedChange]
  );

  const resolvedPlaceholder =
    placeholder ??
    t("views.vendors.vendorItemFields.notesPlaceholder", {
      max: VENDOR_ITEM_NOTES_MAX_LENGTH,
    });

  const limitTitle = t("views.vendors.vendorItemFields.notesMaxLength", {
    max: VENDOR_ITEM_NOTES_MAX_LENGTH,
  });
  const hoverTitle = value.trim() ? value : limitTitle;

  const updatePanelPosition = useCallback(() => {
    const anchor = inputRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const panelHeight = panelRef.current?.offsetHeight ?? 120;
    const gap = 4;
    const width = Math.min(Math.max(rect.width, 256), window.innerWidth - 16);
    const anchorCenter = rect.left + rect.width / 2;
    let left = anchorCenter - width / 2;
    if (left + width > window.innerWidth - 8) {
      left = window.innerWidth - width - 8;
    }
    left = Math.max(8, left);

    const top = Math.max(8, rect.top - panelHeight - gap);
    setPanelPosition({ top, left, width });
  }, []);

  useLayoutEffect(() => {
    if (!expanded) {
      setPanelPosition(null);
      return;
    }
    updatePanelPosition();
  }, [expanded, updatePanelPosition, value]);

  useEffect(() => {
    if (!expanded) return;

    const raf = requestAnimationFrame(updatePanelPosition);
    window.addEventListener("resize", updatePanelPosition);
    window.addEventListener("scroll", updatePanelPosition, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", updatePanelPosition);
      window.removeEventListener("scroll", updatePanelPosition, true);
    };
  }, [expanded, updatePanelPosition]);

  useEffect(() => {
    if (expanded && textareaRef.current) {
      textareaRef.current.focus();
      const len = value.length;
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [expanded, value.length]);

  useEffect(() => {
    if (!expanded) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        wrapperRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      setExpandedState(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [expanded, setExpandedState]);

  const applyPastedText = (
    current: string,
    pasted: string,
    start: number,
    end: number
  ) => {
    onChange(clampVendorItemNotes(current.slice(0, start) + pasted + current.slice(end)));
  };

  const handleInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    if (!pasted) return;
    const input = e.currentTarget;
    applyPastedText(
      value,
      pasted,
      input.selectionStart ?? value.length,
      input.selectionEnd ?? value.length
    );
  };

  const handleTextareaPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    if (!pasted) return;
    const textarea = e.currentTarget;
    applyPastedText(
      value,
      pasted,
      textarea.selectionStart ?? value.length,
      textarea.selectionEnd ?? value.length
    );
  };

  const input = (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => onChange(clampVendorItemNotes(e.target.value))}
      onPaste={handleInputPaste}
      onFocus={() => !disabled && setExpandedState(true)}
      disabled={disabled}
      className={`${className} ${styles.field}`}
      placeholder={resolvedPlaceholder}
      maxLength={VENDOR_ITEM_NOTES_MAX_LENGTH}
      title={hoverTitle}
      readOnly={expanded}
    />
  );

  const expandPanel =
    expanded &&
    !disabled &&
    panelPosition &&
    createPortal(
      <div
        ref={panelRef}
        className={styles.expandPanel}
        style={{
          top: panelPosition.top,
          left: panelPosition.left,
          width: panelPosition.width,
        }}
      >
        <div className={styles.charCount}>
          {t("views.vendors.vendorItemFields.notesCharacterLimit", {
            count: value.length,
            max: VENDOR_ITEM_NOTES_MAX_LENGTH,
          })}
        </div>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(clampVendorItemNotes(e.target.value))}
          onPaste={handleTextareaPaste}
          className={styles.expandTextarea}
          maxLength={VENDOR_ITEM_NOTES_MAX_LENGTH}
          rows={4}
          placeholder={resolvedPlaceholder}
          title={limitTitle}
        />
      </div>,
      document.body
    );

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      {value.trim() && !expanded ? (
        <TooltippedElement placement="top" message={value} element={input} />
      ) : (
        input
      )}
      {expandPanel}
    </div>
  );
}
